import type { ReactNode } from "react";
import { ErrorState, LoadingState, OfflineState } from "@/ui";
import { useUiStore } from "@/app";
import type { ApiError } from "@/api";

/**
 * QueryBoundary — wires four of the five mandatory states (hors-ligne / chargement / erreur, and
 * leaves vide to the child since "empty" is data-shaped). ForbiddenState is handled upstream by
 * ProtectedRoute. Keeps every LIS screen's state handling identical and exhaustive.
 *
 *   <QueryBoundary q={automates}>{(data) => <Table rows={data} … />}</QueryBoundary>
 */
export function QueryBoundary<T>({
  q,
  children,
  loadingVariant = "table",
}: {
  q: {
    data: T | undefined;
    isLoading: boolean;
    isError: boolean;
    error: ApiError | null;
    refetch: () => void;
  };
  children: (data: T) => ReactNode;
  loadingVariant?: "spinner" | "table";
}) {
  const online = useUiStore((s) => s.online);

  if (!online && q.data === undefined) {
    return <OfflineState onRetry={q.refetch} cached={false} />;
  }
  if (q.isLoading) return <LoadingState variant={loadingVariant} />;
  if (q.isError) {
    return (
      <ErrorState
        message={q.error?.message ?? "Impossible de charger les données."}
        {...(q.error?.code ? { code: q.error.code } : {})}
        onRetry={q.refetch}
      />
    );
  }
  if (q.data === undefined) return <LoadingState variant={loadingVariant} />;
  return <>{children(q.data)}</>;
}
